/** D-126: the variables typed for this contract (dates, object and special terms), changed while
 * the contract is a draft; frozen once it is numbered. */

import { type ContractDetail, useContractsItemsPartialUpdate } from "@agencyos/api-client";
import { Button, TextAreaField, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { errorText, useRefreshContracts } from "./shared";

export function TermsForm({ contract, editable }: { contract: ContractDetail; editable: boolean }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const save = useContractsItemsPartialUpdate({ mutation: { onSuccess: refresh } });
  const language = contract.language ?? "fr";
  const texts = (contract.custom_text ?? {}) as Record<string, string>;
  const [start, setStart] = useState(contract.start_date ?? "");
  const [end, setEnd] = useState(contract.end_date ?? "");
  const [text, setText] = useState(texts[language] ?? "");
  const errors = fieldErrors(save.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      id: contract.id,
      data: { start_date: start || null, end_date: end || null, custom_text: { ...texts, [language]: text.trim() } },
    });
  };

  return (
    <form className="stack" onSubmit={onSubmit} noValidate>
      <div className="grid-2">
        <TextField label={t("contracts.manual.start")} type="date" value={start} disabled={!editable} error={errors.start_date} onChange={(e) => setStart(e.target.value)} />
        <TextField label={t("contracts.manual.end")} type="date" value={end} min={start || undefined} disabled={!editable} error={errors.end_date} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <TextAreaField
        label={t("contracts.manual.object")}
        hint={t("contracts.manual.objectHint")}
        rows={3}
        maxLength={8000}
        value={text}
        disabled={!editable}
        onChange={(e) => setText(e.target.value)}
      />
      {save.isError && !errors.start_date && !errors.end_date ? (
        <p role="alert" className="ko">
          {errorText(save.error, t("common.error"))}
        </p>
      ) : null}
      {editable ? (
        <div className="row">
          <Button type="submit" variant="secondary" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
